'use client'

import { useAtom, useSetAtom } from 'jotai'
import { useCallback, useEffect } from 'react'

import type { ChatMessage } from '@/lib/types'
import {
  activeThreadIdAtomFamily,
  autoResumeAtom,
  chatIdAtomFamily,
  createChatThreadId,
  messagesAtomFamily,
  registerChatThreadAtom,
  sendingStateAtomFamily,
  welcomeFlowKeyAtomFamily,
} from '@/state/chat'
import type { SessionDetail } from '@/types/generated/chat_api'

export function useChatMessages(threadId: string) {
  const [messages, setMessages] = useAtom(messagesAtomFamily(threadId))
  const [autoResume] = useAtom(autoResumeAtom)
  const [sendingState, setSendingState] = useAtom(sendingStateAtomFamily(threadId))
  const [welcomeFlowKey, setWelcomeFlowKey] = useAtom(welcomeFlowKeyAtomFamily(threadId))
  const [chatId, setChatId] = useAtom(chatIdAtomFamily(threadId))

  const sendMessage = useCallback(
    async (message: ChatMessage) => setMessages((prev) => [...prev, message]),
    [setMessages]
  )

  const resetChat = useCallback(() => {
    setMessages([])
    setWelcomeFlowKey(0)
    setSendingState('ready')
    setChatId('')
  }, [setMessages, setWelcomeFlowKey, setSendingState, setChatId])

  const loadSession = useCallback(
    (session: SessionDetail) => {
      const loaded: ChatMessage[] = session.turns.flatMap((turn) => [
        {
          id: `${session.sessionId}-user-${turn.turnNumber}`,
          role: 'user' as const,
          parts: [{ type: 'text' as const, text: turn.prompt }],
        },
        {
          id: `${session.sessionId}-ai-${turn.turnNumber}`,
          role: 'assistant' as const,
          parts: [{ type: 'text' as const, text: turn.aiResponse }],
        },
      ])
      setMessages(loaded)
      setChatId(session.sessionId)
      setSendingState('ready')
    },
    [setMessages, setChatId, setSendingState]
  )

  return {
    messages,
    chatId,
    sendingState,
    autoResume,
    welcomeFlowKey,
    setMessages,
    setSendingState,
    setWelcomeFlowKey,
    setChatId,
    sendMessage,
    resetChat,
    loadSession,
  }
}

export function useSurfaceChatThread({
  surfaceId,
  cardId,
  source,
}: {
  surfaceId: string
  cardId?: string
  source: 'widget' | 'sheet' | 'panel'
}) {
  const [threadId, setThreadId] = useAtom(activeThreadIdAtomFamily(surfaceId))
  const register = useSetAtom(registerChatThreadAtom)

  useEffect(() => {
    const now = Date.now()
    register({
      threadId,
      surfaceId,
      cardId,
      source,
      title: '',
      createdAt: now,
      updatedAt: now,
    })
  }, [cardId, register, source, surfaceId, threadId])

  const newThread = useCallback(() => {
    const next = createChatThreadId()
    const now = Date.now()
    register({
      threadId: next,
      surfaceId,
      cardId,
      source,
      title: '',
      createdAt: now,
      updatedAt: now,
    })
    setThreadId(next)
    return next
  }, [cardId, register, setThreadId, source, surfaceId])

  return { threadId, setThreadId, newThread }
}
